import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { ApiError, api, clearToken, getToken, setToken, UNAUTHORIZED_EVENT } from "@/lib/api";

export type User = {
  id: string;
  email: string;
  username: string;
  balance: number;
  role?: string;
  is_email_verified?: boolean;
};

type AuthResult = { ok: boolean; error?: string };

type AuthCtx = {
  user: User | null;
  ready: boolean;
  /** True when a session existed but the server rejected it — the UI can explain why. */
  sessionExpired: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (
    email: string,
    password: string,
    username: string,
    refCode?: string,
  ) => Promise<AuthResult>;
  logout: () => void;
  deductBalance: (usd: number) => boolean;
  refreshUser: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      setUser(await api.get<User>("/api/users/me"));
    } catch (error) {
      // apiFetch already cleared the token and emitted UNAUTHORIZED_EVENT on a 401.
      // Anything else (offline, 500) must not silently log the user out.
      if (error instanceof ApiError && error.status === 401) setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    fetchUser().finally(() => setReady(true));
  }, [fetchUser]);

  useEffect(() => {
    // One listener for the whole app: whenever any request gets a 401, the session
    // is over. Previously each screen failed on its own and the UI kept pretending
    // the user was still signed in.
    const onUnauthorized = () => {
      setUser((previous) => {
        if (previous) setSessionExpired(true);
        return null;
      });
    };
    const onBalanceUpdated = () => void refreshUser();

    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    window.addEventListener("balance_updated", onBalanceUpdated);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
      window.removeEventListener("balance_updated", onBalanceUpdated);
    };
  }, [refreshUser]);

  const login: AuthCtx["login"] = async (email, password) => {
    try {
      const data = await api.post<{ token: string; user: User }>(
        "/api/auth/login",
        { identifier: email, password },
        // A 401 here means bad credentials, not an expired session.
        { anonymous: true, skipAuthRedirect: true },
      );
      setToken(data.token);
      setUser(data.user);
      setSessionExpired(false);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, error: error.code ?? error.message };
      }
      return { ok: false, error: "Network error" };
    }
  };

  const register: AuthCtx["register"] = async (email, password, username, customRefCode) => {
    const refCode =
      customRefCode?.trim() ||
      (typeof window !== "undefined"
        ? localStorage.getItem("vh_ref_code") || localStorage.getItem("ref_code")
        : null);
    try {
      await api.post(
        "/api/auth/register",
        { email, password, username, ref_code: refCode || undefined },
        { anonymous: true, skipAuthRedirect: true },
      );
      // No auto-login: the address has to be verified first.
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, error: error.code ?? error.message };
      }
      return { ok: false, error: "Network error" };
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setSessionExpired(false);
  };

  const deductBalance = (usd: number) => {
    if (!user || user.balance < usd) return false;
    // Optimistic only — the backend is what actually moves the balance.
    setUser({ ...user, balance: Number((user.balance - usd).toFixed(2)) });
    return true;
  };

  return (
    <Ctx.Provider
      value={{ user, ready, sessionExpired, login, register, logout, deductBalance, refreshUser }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
